import type { AnyValueMap } from '@opentelemetry/api-logs';
import { emitFatalLog } from './logger';
import { serializeError } from './serializeError';
import { CONFIG } from 'config/environment_variables';
import { LOCAL_STORAGE_KEYS } from 'constant/localStorage';
import { SESSION_STORAGE_KEYS } from 'constant/sessionStorage';
import { getPreviousNavigationPath } from './navigationTracker';

/** Vite custom event — see https://vite.dev/guide/build#load-error-handling */
export type VitePreloadErrorEvent = Event & {
  payload: unknown;
  preventDefault(): void;
};

export type VitePreloadFailureKind =
  | 'dynamic_import_fetch'
  | 'module_script_load'
  | 'mime_type_mismatch'
  | 'unknown';

export type VitePreloadErrorDetails = {
  summary: string;
  failureKind: VitePreloadFailureKind;
  chunkUrl?: string;
  chunkFile?: string;
  chunkHash?: string;
  error: AnyValueMap;
  context: AnyValueMap;
  http?: AnyValueMap;
};

/** @deprecated kept for cleanup in clearVitePreloadReloadGuard */
const PRELOAD_RELOAD_KEY = 'vite_preload_error_reloaded';
const PRELOAD_RELOAD_COUNT_KEY = 'vite_preload_reload_count';
const PRELOAD_RELOAD_AT_KEY = 'vite_preload_reload_at';
export const VITE_PRELOAD_ERROR_DETECTED = 'vite:preloadError:detected';

function getPreloadReloadCount(): number {
  return parseInt(sessionStorage.getItem(PRELOAD_RELOAD_COUNT_KEY) ?? '0', 10);
}

function bumpPreloadReloadCount(): number {
  const next = getPreloadReloadCount() + 1;
  sessionStorage.setItem(PRELOAD_RELOAD_COUNT_KEY, String(next));
  sessionStorage.setItem(PRELOAD_RELOAD_AT_KEY, String(Date.now()));
  return next;
}
const CHUNK_PROBE_TIMEOUT_MS = 3000;

const CHUNK_URL_IN_MESSAGE =
  /(?:Failed to fetch dynamically imported module|error loading dynamically imported module|Loading (?:CSS )?chunk \d+ failed(?:\.\s*)?(?:\([^)]*\))?):\s*(https?:\/\/[^\s'"]+|\/[^\s'"]+)/i;

const ASSET_URL_PATTERN =
  /(?:https?:\/\/[^\s'"]+|\/[^\s'"]+)assets\/[^\s'")]+?\.(?:js|css|mjs)(?:\?[^\s'"]*)?/gi;

const HASHED_ASSET_FILENAME = /^(.+)-([a-f0-9]{8,})\.(js|css|mjs)$/i;

function classifyFailureKind(message: string): VitePreloadFailureKind {
  const normalized = message.toLowerCase();

  if (
    normalized.includes('mime type') ||
    normalized.includes('text/html') ||
    normalized.includes('expected a javascript')
  ) {
    return 'mime_type_mismatch';
  }

  if (
    normalized.includes('failed to fetch dynamically imported module') ||
    normalized.includes('error loading dynamically imported module') ||
    normalized.includes('loading chunk') ||
    normalized.includes('failed to fetch')
  ) {
    return 'dynamic_import_fetch';
  }

  if (normalized.includes('importing a module script failed')) {
    return 'module_script_load';
  }

  return 'unknown';
}

function parseChunkFilename(url: string): {
  chunkFile?: string;
  chunkHash?: string;
} {
  try {
    const pathname = new URL(url, window.location.origin).pathname;
    const chunkFile = pathname.split('/').pop();
    if (!chunkFile) return {};

    const hashMatch = chunkFile.match(HASHED_ASSET_FILENAME);
    if (!hashMatch) {
      return { chunkFile };
    }

    return {
      chunkFile,
      chunkHash: hashMatch[2],
    };
  } catch {
    const chunkFile = url.split('/').pop()?.split('?')[0];
    if (!chunkFile) return {};
    const hashMatch = chunkFile.match(HASHED_ASSET_FILENAME);
    return hashMatch ? { chunkFile, chunkHash: hashMatch[2] } : { chunkFile };
  }
}

function collectErrorMessages(error: unknown): string[] {
  const messages: string[] = [];
  let current: unknown = error;

  while (current != null) {
    if (typeof current === 'string') {
      messages.push(current);
      break;
    }

    if (current instanceof Error) {
      messages.push(current.message);
      if (current.stack) {
        messages.push(current.stack);
      }
      current = (current as Error & { cause?: unknown }).cause;
      continue;
    }

    if (typeof current === 'object' && 'message' in current) {
      const message = (current as { message?: unknown }).message;
      if (typeof message === 'string') {
        messages.push(message);
      }
    }

    break;
  }

  return messages;
}

function extractChunkUrl(error: unknown): string | undefined {
  const haystack = collectErrorMessages(error).join('\n');

  const messageMatch = haystack.match(CHUNK_URL_IN_MESSAGE);
  if (messageMatch?.[1]) {
    return messageMatch[1].replace(/['"]+$/, '');
  }

  const assetMatches = haystack.match(ASSET_URL_PATTERN);
  if (assetMatches?.length) {
    return assetMatches[assetMatches.length - 1];
  }

  return undefined;
}

function serializeResponseHeaders(headers: Headers): AnyValueMap {
  const result: AnyValueMap = {};
  headers.forEach((value, key) => {
    result[key] = value;
  });
  return result;
}

function headerValue(
  http: AnyValueMap | undefined,
  name: string,
): string | undefined {
  const headers = http?.responseHeaders;
  if (headers == null || typeof headers !== 'object') return undefined;

  const value = (headers as Record<string, unknown>)[name.toLowerCase()];
  return typeof value === 'string' ? value : undefined;
}

/**
 * Browsers do not attach HTTP response metadata to failed `import()` errors.
 * Probe the chunk URL directly so SigNoz receives status + headers (e.g.
 * content-type, cache-control) for stale-deploy diagnosis.
 */
async function probeChunkHttpResponse(chunkUrl: string): Promise<AnyValueMap> {
  const resolvedUrl = new URL(chunkUrl, window.location.origin).href;
  const controller = new AbortController();
  const timeoutId = window.setTimeout(
    () => controller.abort(),
    CHUNK_PROBE_TIMEOUT_MS,
  );

  try {
    let method: 'HEAD' | 'GET' = 'HEAD';
    let response = await fetch(resolvedUrl, {
      method: 'HEAD',
      cache: 'no-store',
      credentials: 'same-origin',
      signal: controller.signal,
    });

    if (response.status === 405 || response.status === 501) {
      method = 'GET';
      response = await fetch(resolvedUrl, {
        method: 'GET',
        cache: 'no-store',
        credentials: 'same-origin',
        signal: controller.signal,
      });
    }

    return {
      url: resolvedUrl,
      method,
      status: response.status,
      statusText: response.statusText,
      redirected: response.redirected,
      responseUrl: response.url,
      responseHeaders: serializeResponseHeaders(response.headers),
    };
  } catch (probeError) {
    const serialized = serializeError(probeError);
    return {
      url: resolvedUrl,
      probeError:
        typeof serialized.message === 'string'
          ? serialized.message
          : 'chunk HTTP probe failed',
    };
  } finally {
    window.clearTimeout(timeoutId);
  }
}

export function parseVitePreloadError(
  event: VitePreloadErrorEvent,
): VitePreloadErrorDetails {
  const importError = event.payload;
  const serializedError = serializeError(importError);
  const errorMessage =
    typeof serializedError.message === 'string' ? serializedError.message : '';
  const failureKind = classifyFailureKind(errorMessage);
  const chunkUrl = extractChunkUrl(importError);
  const { chunkFile, chunkHash } = chunkUrl ? parseChunkFilename(chunkUrl) : {};

  const summary = chunkFile
    ? `Vite chunk preload failed: ${chunkFile} (${failureKind})`
    : `Vite chunk preload failed (${failureKind})`;

  return {
    summary,
    failureKind,
    chunkUrl,
    chunkFile,
    chunkHash,
    error: serializedError,
    context: {
      pageHref: window.location.href,
      pagePath: window.location.pathname,
      fromPath: getPreviousNavigationPath() || undefined,
      referrer: document.referrer || undefined,
      appVersion: __APP_BUILD_VERSION__,
      userAgent: navigator.userAgent,
      userId: localStorage.getItem(LOCAL_STORAGE_KEYS.USER_ID) ?? 'UnknownUser',
      userEmail:
        sessionStorage.getItem(SESSION_STORAGE_KEYS.EMAIL) ?? 'UnknownEmail',
      reloadCount: getPreloadReloadCount(),
    },
  };
}

export async function reportVitePreloadError(
  event: VitePreloadErrorEvent,
  options?: { reloadAlreadyAttempted?: boolean },
): Promise<VitePreloadErrorDetails> {
  const details = parseVitePreloadError(event);
  const http = details.chunkUrl
    ? await probeChunkHttpResponse(details.chunkUrl)
    : undefined;
  const detailsWithHttp = { ...details, http };

  const userId =
    localStorage.getItem(LOCAL_STORAGE_KEYS.USER_ID) ?? 'UnknownUser';
  const userEmail =
    sessionStorage.getItem(SESSION_STORAGE_KEYS.EMAIL) ?? 'UnknownEmail';

  emitFatalLog({
    body: {
      message: details.summary,
      failureKind: details.failureKind,
      chunk: {
        url: details.chunkUrl,
        file: details.chunkFile,
        hash: details.chunkHash,
      },
      error: details.error,
      http,
      context: details.context,
      reloadAlreadyAttempted: options?.reloadAlreadyAttempted ?? false,
    },
    attrs: {
      source: 'ViteCrash',
      'user.id': userId,
      'user.email': userEmail,
      failureKind: details.failureKind,
      chunkUrl: details.chunkUrl,
      chunkFile: details.chunkFile,
      chunkHash: details.chunkHash,
      errorName:
        typeof details.error.name === 'string' ? details.error.name : undefined,
      errorMessage:
        typeof details.error.message === 'string'
          ? details.error.message
          : undefined,
      httpStatus: typeof http?.status === 'number' ? http.status : undefined,
      httpContentType: headerValue(http, 'content-type'),
      httpCacheControl: headerValue(http, 'cache-control'),
      httpEtag: headerValue(http, 'etag'),
      appVersion: __APP_BUILD_VERSION__,
      reloadCount: getPreloadReloadCount(),
      reloadAlreadyAttempted: options?.reloadAlreadyAttempted ?? false,
    },
  });

  return detailsWithHttp;
}

/**
 * Handles Vite dynamic-import preload failures (stale chunks after deploy).
 *
 * - Dev mode: logs only. Vite's HMR handles module recovery — reloading would
 *   cause disruptive loops during normal navigation in local development.
 * - Production: logs, then cache-bust reloads once. On repeat failure, notifies
 *   AppVersionUpdateListener instead of looping infinitely.
 *
 * @see https://vite.dev/guide/build#load-error-handling
 */
export function handleVitePreloadError(event: VitePreloadErrorEvent): void {
  void handleVitePreloadErrorAsync(event);
}

async function handleVitePreloadErrorAsync(
  event: VitePreloadErrorEvent,
): Promise<void> {
  // Always call preventDefault() first. If omitted, Vite's own vite:preloadError
  // default handler fires — in Vite 5+ / 8+ dev mode this triggers a full hard
  // page reload, which is exactly the "increased reloads during navigation" symptom.
  event.preventDefault();

  if (CONFIG.NODE_ENV === 'development') {
    // Dev mode: HMR handles module recovery. Skip the HTTP chunk probe (dev
    // server URLs are not hashed assets) and just log the error for visibility.
    const details = parseVitePreloadError(event);
    emitFatalLog({
      body: {
        message: details.summary,
        failureKind: details.failureKind,
        error: details.error,
        context: details.context,
      },
      attrs: {
        source: 'ViteCrash',
        'user.id':
          localStorage.getItem(LOCAL_STORAGE_KEYS.USER_ID) ?? 'UnknownUser',
        'user.email':
          sessionStorage.getItem(SESSION_STORAGE_KEYS.EMAIL) ?? 'UnknownEmail',
        failureKind: details.failureKind,
        chunkFile: details.chunkFile,
        appVersion: __APP_BUILD_VERSION__,
        isDev: true,
      },
    });
    return;
  }

  const reloadAlreadyAttempted = getPreloadReloadCount() >= 1;

  if (reloadAlreadyAttempted) {
    // No reload is about to happen on this pass, so there's no rush — await the
    // full report (including the HTTP probe) before notifying the fallback UI.
    await reportVitePreloadError(event, { reloadAlreadyAttempted });
    window.dispatchEvent(new Event(VITE_PRELOAD_ERROR_DETECTED));
    return;
  }

  // Kick off the recovery reload immediately, before anything async. Previously
  // this awaited reportVitePreloadError() first — which includes an HTTP probe
  // with up to a 3s timeout — so React could render the broken chunk (payload
  // resolved to undefined once event.preventDefault() suppressed the rethrow
  // above) and hit the ErrorBoundary well before the reload ever fired.
  //
  // reportVitePreloadError() itself is untouched — same fields, same reloadCount
  // ordering, same HTTP probe — it just now runs fire-and-forget instead of
  // gating the reload. The only change in behaviour is a (pre-existing, already
  // rare) chance that a slow network cuts this specific log short if the page
  // unloads before it completes — the report content/shape is unaffected.
  const url = new URL(window.location.href);
  url.searchParams.set('_cb', String(Date.now()));
  window.location.href = url.toString();

  void reportVitePreloadError(event, { reloadAlreadyAttempted });
  bumpPreloadReloadCount();
}

export function clearVitePreloadReloadGuard(): void {
  // Clear both the legacy key and the new counter keys.
  sessionStorage.removeItem(PRELOAD_RELOAD_KEY);
  sessionStorage.removeItem(PRELOAD_RELOAD_COUNT_KEY);
  sessionStorage.removeItem(PRELOAD_RELOAD_AT_KEY);
}
